import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "../styles/institution.css";
import { AppShell } from "../components/AppShell";
import { EmptyState } from "../components/EmptyState";
import { PaginationBar, usePagination } from "../components/Pagination";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../hooks/useAuth";
import { useT } from "../i18n/LanguageProvider";
import {
  fetchInstitutionBilling,
  fetchOrganisationBilling,
  downloadInvoicePdf,
  downloadSubscriptionInvoicePdf,
} from "../api/billing";
import {
  listInstitutionRequests,
  listOrganisationRequests,
} from "../api/verifications";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePrice } from "../hooks/usePrice";
import { C, font } from "../styles/tokens";
import { formatRupees } from "../utils/money";
import {
  isRefund,
  ledgerAmountText,
  ledgerStatusText,
  payoutBalanceView,
} from "../utils/ledger";

export function EarningsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { localUser } = useAuth();
  const isInstitution = localUser?.type === "INSTITUTION_OFFICER";
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const subscriptionType = isInstitution
    ? "INSTITUTION_SUBSCRIPTION"
    : "ORGANISATION_SUBSCRIPTION";
  const fetcher = isInstitution
    ? fetchInstitutionBilling
    : fetchOrganisationBilling;
  const listFn = isInstitution
    ? listInstitutionRequests
    : listOrganisationRequests;

  async function reload() {
    setLoading(true);
    try {
      const [billing, pendingRequests] = await Promise.all([
        fetcher(),
        listFn("PENDING").catch(() => []),
      ]);

      setData({
        ...billing,
        pendingRequestsCount: pendingRequests?.length ?? 0,
      });
      setError(null);
    } catch (err) {
      setError(err.message ?? "Could not load billing");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isInstitution || isOrganisation) reload();
  }, [isInstitution, isOrganisation]);

  async function handlePay() {
    navigate("/checkout", {
      state: {
        type: subscriptionType,
        provider: "CASHFREE",
      },
    });
  }

  if (!isInstitution && !isOrganisation) {
    return (
      <AppShell>
        <div style={cardStyle}>
          This page is for institution and organisation accounts.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth="100%">
      <div style={{ width: "100%", paddingBottom: 40 }}>
        <h1
          style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 800,
            color: "#0f172a",
            margin: isMobile ? "0 0 16px" : "0 0 20px",
            letterSpacing: "-0.02em",
          }}
        >
          Payments
        </h1>

        {error && (
          <div
            style={{
              background: "#fef2f2",
              color: "#dc2626",
              padding: 14,
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* 4 KPI Payment Stat Cards connected to Real-Time Data */}
        <PaymentStatCards loading={loading} data={data} />

        {/* Subscription details */}
        <SubscriptionCard
          subscription={data?.subscription}
          loading={loading}
          onPay={handlePay}
          paying={paying}
          payError={payError}
          isInstitution={isInstitution}
        />

        {/* Period Earnings & Decision Analytics */}
        <PeriodEarningsCard loading={loading} ledger={data?.ledger ?? []} />

        {/* Earnings Summary */}
        <EarningsSummary loading={loading} earnings={data?.earnings} />

        {/* Verification Ledger */}
        <LedgerSection loading={loading} ledger={data?.ledger ?? []} />

        <div
          style={{
            fontSize: 12,
            color: C.textLight,
            marginTop: 16,
            lineHeight: 1.5,
          }}
        >
          Automated payouts to your registered bank account will start in a
          future update (Cashfree Payouts, monthly cycle). Until then, the
          payable balance accumulates here. Contact platform support to request
          an early payout.
        </div>
      </div>
    </AppShell>
  );
}

/* =============================================================================
   PaymentStatCards Component - Real Time KPI Cards
   ============================================================================= */

function PaymentStatCards({ loading, data }) {
  const isMobile = useIsMobile();
  const earnings = data?.earnings ?? {};
  const ledger = data?.ledger ?? [];

  // Real-time calculated financial values from backend
  const totalEarnedPaise = earnings.totalEarnedPaise ?? 0;
  const verificationsCount = earnings.verificationsCount ?? 0;
  const payablePaise = earnings.payablePaise ?? 0;

  // What was earned since the first of this month
  const now = new Date();
  const firstOfThisMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
  ).getTime();
  const thisMonthEntries = ledger.filter(
    (e) => new Date(e.createdAt).getTime() >= firstOfThisMonth,
  );
  const thisMonthEarnedPaise = thisMonthEntries.reduce(
    (sum, e) => sum + (e.amountPaise ?? 0),
    0,
  );

  const pendingCount =
    data?.pendingRequestsCount ?? ledger.filter((e) => !e.settledAt).length;

  const totalCollectedText = formatRupees(totalEarnedPaise);
  const successCountText = verificationsCount.toLocaleString("en-IN");
  const pending = payoutBalanceView(
    payablePaise,
    pendingCount === 1
      ? "1 payment to confirm"
      : `${pendingCount} payments to confirm`,
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "1fr"
          : "repeat(auto-fit, minmax(220px, 1fr))",
        gap: isMobile ? 12 : 16,
        marginBottom: 24,
      }}
    >
      {/* Card 1: Total collected */}
      <HoverablePaymentKpiCard
        label="Total collected"
        value={loading ? "..." : totalCollectedText}
        subtext={`${formatRupees(thisMonthEarnedPaise)} this month`}
        subtextColor="#64748b"
        accentColor="#2563eb"
        hoverBg="#eff6ff"
      />

      {/* Card 2: Successful payments */}
      <HoverablePaymentKpiCard
        label="Successful payments"
        value={loading ? "..." : successCountText}
        subtext={null}
        subtextColor="#64748b"
        accentColor="#16a34a"
        hoverBg="#f0fdf4"
      />

      {/* Card 3: Pending amount */}
      <HoverablePaymentKpiCard
        label="Pending amount"
        value={loading ? "..." : pending.value}
        subtext={pending.subtext}
        subtextColor="#d97706"
        accentColor="#d97706"
        hoverBg="#fffbeb"
      />
    </div>
  );
}

function HoverablePaymentKpiCard({
  label,
  value,
  subtext,
  subtextColor,
  accentColor,
}) {
  const [hovered, setHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? accentColor : "#ffffff",
        borderRadius: 14,
        borderTop: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderRight: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderBottom: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderLeft: `5px solid ${accentColor}`,
        padding: isMobile ? "16px" : "20px 24px",
        boxShadow: hovered
          ? `0 14px 28px -4px ${accentColor}55, 0 8px 12px -6px ${accentColor}30`
          : "0 1px 3px rgba(0,0,0,0.03)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: hovered ? "#ffffff" : "#64748b",
          transition: "color 0.2s",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: isMobile ? 24 : 28,
          fontWeight: 800,
          color: hovered ? "#ffffff" : "#0f172a",
          margin: "8px 0 4px",
          fontVariantNumeric: "tabular-nums",
          transition: "color 0.2s",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: hovered ? "#ffffff" : subtextColor,
          transition: "color 0.2s",
        }}
      >
        {subtext}
      </div>
    </div>
  );
}

const kpiCardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "20px 24px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const kpiLabelStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: "#64748b",
  marginBottom: 8,
};

const kpiValueStyle = {
  fontSize: 28,
  fontWeight: 800,
  color: "#0f172a",
  letterSpacing: "-0.02em",
  marginBottom: 6,
  fontVariantNumeric: "tabular-nums",
};

// ----- Subcomponents -----

function PeriodEarningsCard({ loading, ledger }) {
  const isMobile = useIsMobile();
  const today = isoDay(new Date());
  const monthAgo = isoDay(new Date(Date.now() - 30 * 86_400_000));
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  if (loading) {
    return (
      <section
        style={{
          ...cardStyle,
          padding: isMobile ? "16px 14px" : 24,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: C.textMed }}>Loading...</div>
      </section>
    );
  }

  const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
  const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
  const inRange = (ledger ?? []).filter((e) => {
    const stamp = e.settledAt ?? e.createdAt;
    if (!stamp) return false;
    const t = new Date(stamp).getTime();
    return t >= fromMs && t <= toMs;
  });
  const decisions = inRange.length;
  const revenuePaise = inRange.reduce((s, e) => s + (e.amountPaise ?? 0), 0);

  return (
    <section
      style={{
        ...cardStyle,
        padding: isMobile ? "16px 14px" : 24,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "baseline",
          gap: isMobile ? 6 : 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          Decisions and revenue
        </h2>
        <div style={{ fontSize: 12, color: C.textMed }}>
          Pick a date range to see how much your team handled in the period.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 12,
          marginTop: 12,
        }}
        data-responsive-grid="2"
      >
        <DateField
          label="From"
          value={from}
          onChange={setFrom}
          max={to || today}
          isMobile={isMobile}
        />
        <DateField
          label="To"
          value={to}
          onChange={setTo}
          min={from}
          max={today}
          isMobile={isMobile}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
          marginTop: 14,
        }}
      >
        <Stat label="Decisions" value={decisions} />
        <Stat
          label="Revenue earned"
          value={`Rs. ${(revenuePaise / 100).toLocaleString("en-IN")}`}
        />
      </div>
    </section>
  );
}

function DateField({ label, value, onChange, min, max, isMobile }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <input
        type="date"
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          minHeight: isMobile ? 44 : undefined,
          fontSize: 13,
          fontFamily: font,
          border: `1.5px solid ${C.dividerStrong}`,
          borderRadius: 6,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div
      style={{
        background: C.bgMuted,
        border: `1px solid ${C.divider}`,
        borderRadius: 6,
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.textMed,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: C.textDark,
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function isoDay(d) {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function SubscriptionCard({
  subscription,
  loading,
  onPay,
  paying,
  payError,
  isInstitution,
}) {
  const isMobile = useIsMobile();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // What the server charges for the year, read before paying.
  const yearPaise = usePrice(
    isInstitution ? "INSTITUTION_SUBSCRIPTION" : "ORGANISATION_SUBSCRIPTION",
  ).pricePaise;

  if (loading) {
    return (
      <section
        style={{
          ...cardStyle,
          padding: isMobile ? "16px 14px" : 24,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: C.textMed }}>
          Loading subscription details...
        </div>
      </section>
    );
  }

  const status = subscription?.status ?? "NONE";
  const styles = {
    NONE: { bg: C.bgMuted, color: C.textMed, label: "None" },
    ACTIVE: { bg: C.greenLight, color: C.green, label: "Active" },
    GRACE_PERIOD: { bg: C.amberLight, color: C.amber, label: "Grace period" },
    EXPIRED: { bg: C.redLight, color: C.red, label: "Expired" },
  }[status] ?? { bg: C.bgMuted, color: C.textMed, label: status };

  const expiresInDays = subscription?.expiresAt
    ? Math.ceil(
        (new Date(subscription.expiresAt).getTime() - Date.now()) /
          (24 * 60 * 60 * 1000),
      )
    : null;
  const showRenewWarning =
    status === "ACTIVE" && expiresInDays !== null && expiresInDays <= 30;
  const needsRenewal =
    status === "NONE" ||
    status === "EXPIRED" ||
    status === "GRACE_PERIOD" ||
    showRenewWarning;

  async function handleDownloadInvoice() {
    setDownloading(true);
    setDownloadError(null);
    const invNo =
      subscription?.invoiceNumber ||
      subscription?.paymentId ||
      "Annual-Subscription";
    const kind = isInstitution ? "institution" : "organisation";

    try {
      // 1. If paymentId is present, try downloading via /payments/:paymentId/invoice-pdf
      if (subscription?.paymentId) {
        try {
          await downloadInvoicePdf(subscription.paymentId, invNo);
          return;
        } catch (directErr) {
          console.warn(
            "Direct payment invoice PDF download failed, falling back to subscription invoice endpoint:",
            directErr,
          );
        }
      }

      // 2. Fallback to /institution/subscription-invoice/pdf or /organisation/subscription-invoice/pdf
      await downloadSubscriptionInvoicePdf(kind, invNo);
    } catch (err) {
      console.error("Invoice download failed:", err);
      let errorMsg =
        err?.message || "Could not download invoice PDF. Please try again.";

      // Extract JSON or text error message from Blob if axios responseType was blob
      if (err?.original?.response?.data instanceof Blob) {
        try {
          const text = await err.original.response.data.text();
          try {
            const parsed = JSON.parse(text);
            errorMsg = parsed?.message || parsed?.error?.message || errorMsg;
          } catch {
            if (text && !text.startsWith("<") && text.length < 200) {
              errorMsg = text;
            }
          }
        } catch {
          // The body could not be read: the message above is shown instead.
        }
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      }

      setDownloadError(errorMsg);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section
      style={{
        ...cardStyle,
        padding: isMobile ? "16px 14px" : 24,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "flex-start",
          marginBottom: 12,
          gap: isMobile ? 14 : 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
            Annual subscription
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "2px 10px",
                fontSize: 11,
                fontWeight: 700,
                color: styles.color,
                background: styles.bg,
                borderRadius: 10,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: styles.color,
                }}
              />
              {styles.label}
            </span>
            {subscription?.expiresAt && (
              <span style={{ fontSize: 13, color: C.textMed }}>
                {status === "ACTIVE" ? "Renews" : "Expired"} on{" "}
                {formatDate(subscription.expiresAt)}
              </span>
            )}
          </div>
          {showRenewWarning && (
            <div style={{ fontSize: 12, color: C.amber, marginTop: 6 }}>
              Subscription expires in {expiresInDays}{" "}
              {expiresInDays === 1 ? "day" : "days"}.
            </div>
          )}
          {status === "GRACE_PERIOD" && (
            <div style={{ fontSize: 12, color: C.amber, marginTop: 6 }}>
              Your subscription has expired and is in a short grace period.
              Renew now to keep verifying credentials.
            </div>
          )}
          {status === "EXPIRED" && (
            <div style={{ fontSize: 12, color: C.red, marginTop: 6 }}>
              Your subscription has expired. Renew it to verify credentials
              again.
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: isMobile ? "stretch" : "flex-end",
            width: isMobile ? "100%" : undefined,
          }}
        >
          {status === "ACTIVE" && (
            <button
              onClick={handleDownloadInvoice}
              disabled={downloading}
              title="Download Tax Invoice PDF for this subscription"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "8px 16px",
                width: isMobile ? "100%" : undefined,
                minHeight: isMobile ? 44 : undefined,
                fontSize: 13,
                fontWeight: 600,
                color: "#0284c7",
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: 6,
                cursor: downloading ? "default" : "pointer",
                fontFamily: font,
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {downloading ? "Downloading..." : "Download Invoice"}
            </button>
          )}

          {needsRenewal && (
            <button
              onClick={() => onPay("CASHFREE")}
              disabled={paying}
              style={{
                display: isMobile ? "flex" : undefined,
                alignItems: isMobile ? "center" : undefined,
                justifyContent: isMobile ? "center" : undefined,
                width: isMobile ? "100%" : undefined,
                minHeight: isMobile ? 44 : undefined,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                color: "white",
                background: paying ? C.textLight : C.green,
                border: 0,
                borderRadius: 4,
                cursor: paying ? "default" : "pointer",
                fontFamily: font,
                whiteSpace: "nowrap",
              }}
            >
              {paying
                ? "Opening..."
                : yearPaise
                  ? `Pay ${formatRupees(yearPaise)} / year`
                  : "Pay the yearly subscription"}
            </button>
          )}
        </div>
      </div>

      {downloadError && (
        <div
          style={{
            fontSize: 12,
            color: C.red,
            padding: "8px 12px",
            background: C.redLight,
            borderRadius: 4,
            marginBottom: 10,
          }}
        >
          {downloadError}
        </div>
      )}

      {payError && (
        <div
          style={{
            fontSize: 12,
            color: C.red,
            padding: "8px 12px",
            background: C.redLight,
            borderRadius: 4,
          }}
        >
          {payError}
        </div>
      )}
    </section>
  );
}

function EarningsSummary({ loading, earnings }) {
  const isMobile = useIsMobile();
  if (loading) return null;
  if (!earnings || earnings.verificationsCount === 0) {
    return (
      <section
        style={{
          ...cardStyle,
          padding: isMobile ? "16px 14px" : 24,
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
          Earnings
        </h2>
        <div style={{ fontSize: 13, color: C.textMed }}>
          You earn a share of the fee for each verification you approve. Once
          approvals begin, you'll see your earnings here.
        </div>
      </section>
    );
  }
  const payable = payoutBalanceView(earnings.payablePaise, "Awaiting payout");
  return (
    <section
      style={{
        ...cardStyle,
        padding: isMobile ? "16px 14px" : 24,
        marginBottom: 16,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>
        Earnings
      </h2>
      <div
        data-responsive-grid="stats"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        <StatCard
          label="Total earned"
          value={formatRupees(earnings.totalEarnedPaise)}
          sub={`${earnings.verificationsCount} verifications`}
        />
        <StatCard
          label="Payable balance"
          value={payable.value}
          sub={payable.subtext}
          highlight={earnings.payablePaise > 0}
        />
        <StatCard
          label="Already paid out"
          value={formatRupees(earnings.settledPaise)}
          sub="To your bank"
        />
      </div>
    </section>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div
      style={{
        padding: 12,
        background: highlight ? C.blueLight : C.bgMuted,
        borderRadius: 6,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.textMed,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: highlight ? C.blueDark : C.textDark,
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function LedgerSection({ loading, ledger }) {
  const isMobile = useIsMobile();
  const pager = usePagination(ledger, 10);
  if (loading) {
    return (
      <section
        style={{
          ...cardStyle,
          padding: isMobile ? "16px 14px" : 24,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, color: C.textMed }}>Loading ledger...</div>
      </section>
    );
  }
  if (ledger.length === 0) {
    return (
      <section
        style={{
          ...cardStyle,
          padding: isMobile ? "16px 14px" : 24,
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>
          Verification ledger
        </h2>
        <EmptyState
          kind="inbox"
          title="No earnings yet"
          body="Every verification you approve earns Rs. 125. Completed verifications appear here."
          size="compact"
        />
      </section>
    );
  }

  return (
    <section
      style={{
        ...cardStyle,
        padding: isMobile ? "16px 14px" : 24,
        marginBottom: 16,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>
        Verification ledger
      </h2>
      {pager.pageItems.map((entry, i) => (
        <div
          key={entry.id}
          style={{
            padding: "10px 0",
            borderTop: i > 0 ? `1px solid ${C.divider}` : "none",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            gap: isMobile ? 6 : 12,
            alignItems: isMobile ? "flex-start" : "flex-start",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textDark }}>
              {entry.credentialTitle ?? "Verification fee"}
            </div>
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
              {formatDate(entry.createdAt)}
              {" \u00B7 "}
              <span style={{ color: ledgerStatusColor(entry) }}>
                {ledgerStatusText(entry, formatDate)}
              </span>
            </div>
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: ledgerAmountColor(entry),
              fontVariantNumeric: "tabular-nums",
              alignSelf: isMobile ? "flex-start" : "auto",
            }}
          >
            {ledgerAmountText(entry)}
          </div>
        </div>
      ))}
      <PaginationBar
        pager={pager}
        itemLabel="entries"
        style={{
          margin: isMobile ? "12px -14px -14px" : "12px -24px -24px",
          borderRadius: "0 0 8px 8px",
        }}
      />
    </section>
  );
}

// ----- Helpers -----

/** A refund reads in red; a share is amber until it is paid out. */
function ledgerStatusColor(entry) {
  if (isRefund(entry)) return C.red;
  return entry.settledAt ? C.green : C.amber;
}

/** A refund takes money back; a share not yet paid out is still to come. */
function ledgerAmountColor(entry) {
  if (isRefund(entry)) return C.red;
  return entry.settledAt ? C.textMed : C.green;
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 14,
  padding: 24,
};
